/**
 * Partner landing page configured by database content
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';


import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import VendorPartnerProgramContainer from './VendorPartnerProgramContainer';
import VendorProgramForm from '../forms/VendorProgramForm';
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material';

const abEnvironment = new AbEnvironment();

const VendorPartnerProgramQuery = graphql`
query VendorPartnerProgramQuery($vppId: UUID!, $partnerId: UUID!) {
  vppTByVppId(vppId: $vppId) {
    vendorId
    programName
    description
    notes
    visibility
    defaultParmValue
    formId
    accountTByVendorId {
      accountName
      logo
    }
    repFname
    repLname
    repEmail
    repImg
    ctaTitle
    ctaText
    ctaButton
    ctaLabel
    ctaTitleSuspend
    ctaTextSuspend
    ctaTitleDecline
    ctaTextDecline
    ctaTitleLeft
    ctaTextLeft
    cpTByCtaCpId {
      programType
      programName
      description
      qualificationPeriod
      autoPayout
      cpLineTsByCpId {
        totalCount
        nodes {
          revenuePeriod
          revenueMin
          incentivePerc
        }
      }
    }
    vppRTsByVppId(condition: {partnerId: $partnerId}) {
      totalCount
      nodes {
        partnerId
        vpprId
        accountTByPartnerId {
          resellerProgramTsByResellerAccountId {
            totalCount
            nodes {
              resellerPId
              cpTByCpId {
                accountId
              }
            }
          }
        }
        stage
        notes
        repFname
        repLname
        repEmail
        repImg
      }
    }
    vppAssetTsByVppId (orderBy: ASSET_ORDER_ASC) {
      totalCount
      nodes {
        stage
        assetType
        assetLink
        assetTitle
      }
    }
    vppContentBlockTsByVppId (condition: {blockType: 0}) {
      totalCount
      nodes {
        stage
        parentBlockId
        vppContentBlockId
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      // marginTop: -32,
    }
});

class VendorPartnerProgram extends Component {

  constructor(props) {
    super(props);

    this.state = {
      mountComplete:     false,
      page:              0,
      rowsPerPage:       5,
      showForm:          false,
      reFetch:           0,
      formSaveDisabled:  true
    };  
  }

  componentDidMount() {
    const { isAuthenticatedReseller, isAuthenticatedAppBind, isAuthenticatedVendor, callPartnerApi } = this.props.auth;

    if (isAuthenticatedVendor() || this.props.mode === 'dev') {
      // This is preview mode for a vendor configuring a reseller experience.  Nothing to hook up.
      this.setState({mountComplete: true});
      return;
    }

    // Now only reseller or appbind should be in.
    if (!isAuthenticatedReseller() && !isAuthenticatedAppBind()) {
      return;
    }

    const params = new URLSearchParams(this.props.location.search);
    let vppId = params.get('vppId');
    let partnerId = abEnvironment.getUserAttribute('a_id');

    // Call server side process for the prospect.
    let payload = {
      operation: "connectPartnerToVendorPartnerProgram",
      params: {
        pAccountId: partnerId,
        vppId:      vppId
      }
    };

    callPartnerApi('appbind/generic',
                                  'POST',
                                  payload)
    .then(response => {
      return response.json();
    })
    .then(data => {
      this.setState({mountComplete: true});
    })
    .catch(err => {
      // console.log('err');
      this.setState({mountComplete: true});
    });
    
  }

  //declaring the functions are part of this component, use arrow notation as "definition"
  openProgramForm = () => {
    this.setState({
      showForm: true
    });
  }

  handleFormClose = () => {
    this.setState({
      showForm: false,
    });
  }

  handleFormSave = () => {
    const { isAuthenticatedVendor } = this.props.auth;
    if (this.state.theSaveFunction) {
      if (isAuthenticatedVendor() || this.props.mode === 'dev') {
        // This is preview mode for a vendor configuring a reseller experience, so don't save.
        this.setState({showForm: false});
        return;
      }

      //execute the "formSave" function from state
      let thePromise = this.state.theSaveFunction();
      // this.theSaveFunction();

      thePromise.finally(() => {
        this.handleFormClose();
    
        //refresh the renderer, since the form was saved.
        let fetchCount = this.state.reFetch;
        fetchCount++;
        this.setState({ reFetch: fetchCount });
      });
    }
  }

  allocateSaveFunction = (action) => {
    //the action passed here is "the save form function" from deep in Form.js with the values from state..
    this.setState({ theSaveFunction: action });
  }

  setFormSaveDisabled = (disable) => {
    this.setState({
      formSaveDisabled: disable
    })
  }

  render() {
    const { isAuthenticated, isAuthenticatedVendor, isAuthenticatedReseller, isAuthenticatedCustomer, isAuthenticatedAppBind, isUnauthenticatedCustomer, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticated() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }    
    if (!isAuthenticatedVendor() && !isAuthenticatedCustomer() && !isUnauthenticatedCustomer() && !isAuthenticatedReseller() && !isAuthenticatedAppBind()) {
      // others cannot see this component
      return ( <Redirect to='/home' /> );
    } 

    // is this a vendor devmode?  Need 2 things for this.
    let devMode = false;
    if (isAuthenticatedVendor() || this.props.mode === 'dev') {
      // For dev mode, both must be true.
      if (isAuthenticatedVendor() && this.props.mode === 'dev') {
        devMode = true;
      } else {
        return ( <Redirect to='/home' /> );
      }
    }

    if (!this.state.mountComplete === true) {
      return (
        <LoadingSpinner />
      );
    }

    let partnerId;
    let vppId;
    let parentForceFetch = 0;
    if (devMode === true) {
      vppId = this.props.vppId;
      partnerId = this.props.partnerId;
      if (partnerId == null) {
        partnerId = '00000000-0000-0000-0000-000000000000';
      }
      parentForceFetch = this.props.refetch;
    } else {
      const params = new URLSearchParams(this.props.location.search);
      vppId = params.get('vppId');
      partnerId = abEnvironment.getUserAttribute('a_id');
    }

    return (
      <div className={classes.root}>

        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={VendorPartnerProgramQuery}
          variables={{vppId: vppId, partnerId: partnerId, forceFetch:this.state.reFetch, parentForceFetch:parentForceFetch}}
          render={({error, props})=> {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // Did nothing come back?  This can be the case if there is no relationship for the partner connecting
              // them to the vendor.
              if (props.vppTByVppId == null) {
                return ( <Redirect to='/home' /> );
              }

              // Setup a vendor program object
              let vpp = props.vppTByVppId;

              let formPresent = false;
              if (vpp.formId != null) {
                formPresent = true;
              }

              // Get relationship
              let relationship = null;

              if (devMode === true) {
                relationship = { 
                  stage: this.props.stage,
                  repFname: vpp.repFname,
                  repLname: vpp.repLname,
                  repEmail: vpp.repEmail,
                  repImg: vpp.repImg
                };
              } else {
                if (props.vppTByVppId.vppRTsByVppId.totalCount > 0) {
                  relationship = props.vppTByVppId.vppRTsByVppId.nodes[0];
                }
                
                if (relationship == null) {
                  // There isn't a relationship, default one as stage 0.
                  //return ( <Redirect to='/home' /> );
                  relationship = { stage: 0 };
                }
              }

              let assets = [];
              for (let i=0; i < props.vppTByVppId.vppAssetTsByVppId.nodes.length; i++) {
                let asset = props.vppTByVppId.vppAssetTsByVppId.nodes[i];
                if (asset.stage === relationship.stage) {
                  assets.push(asset);
                }
              }

              // Get the top level container for the stage.
              let rootContainerId = null;
              for (let i=0; i < props.vppTByVppId.vppContentBlockTsByVppId.nodes.length; i++) {
                let block = props.vppTByVppId.vppContentBlockTsByVppId.nodes[i];
                if (block.stage === relationship.stage && block.parentBlockId == null) {
                  rootContainerId = block.vppContentBlockId;
                  break;
                }
              }

              return (
                <div>
                  <VendorPartnerProgramContainer devMode={devMode} vpp={vpp} relationship={relationship} assets={assets} formPresent={formPresent} showProgramForm={this.openProgramForm} containerBlockId={rootContainerId} topLevel={true} auth={this.props.auth} history={this.props.history}/>
                  { vpp.formId &&
                    <Dialog
                    disableEscapeKeyDown
                    open={this.state.showForm}
                    keepMounted={false}
                    onClose={(event, reason) => {
                      if (reason !== 'backdropClick') {
                        this.setState({
                          showForm: false
                        })
                      }
                    }}
                    maxWidth="md"
                    fullWidth={true}>
                    <DialogContent>
                      <VendorProgramForm
                        accountId={partnerId} formId={vpp.formId} auth={this.props.auth}
                        setFormSaveDisabled={this.setFormSaveDisabled}
                        passSaveFunction={this.allocateSaveFunction}/>
                    </DialogContent>
                    <DialogActions>
                      { this.state.theSaveFunction && relationship.stage === 0 &&
                        <Button
                          color="primary"
                          variant="contained"
                          onClick={this.handleFormSave}
                          disabled={this.state.formSaveDisabled || relationship.stage >= 1}
                          >Save</Button>
                      }
                      <Button 
                        color="primary"
                        variant="outlined"
                        onClick={this.handleFormClose}>Close</Button>
                    </DialogActions>
                  </Dialog>
                  }
                </div>
              );

            } else {
              return (
                <LoadingSpinner />
              );
            }
          }}
        />

      </div>
    );
  }
}

VendorPartnerProgram.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(VendorPartnerProgram);
